import React from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { z } from 'zod';
import { CamundaHistoryService, CamundaTask, CamundaTaskService, CamundaUserService } from '@/lib/camunda';
import { isAuthError, isCamundaRequestError, isConcurrentTaskUpdateError, isTaskNotActiveError } from '@/lib/camunda/base-service';
import { createBpmEventId, trackActionFailed, trackActionStarted, trackActionSucceeded } from '@/lib/telemetry/bpm-telemetry';
import type { TaskRealtimeTaskEvent } from '@/lib/task-realtime/types';
import type { LoadTasksInput, LoadTasksOptions, TaskActionResult, TaskInactiveNotice, TaskSelectionResult, TaskStore } from './task-store.types';

type PendingLocalTaskAction = 'claim' | 'unclaim' | 'reassign' | 'complete';
type AssignmentTelemetryAction = 'claim' | 'unclaim' | 'reassign';
type PendingLocalTaskActionEntry = {
  action: PendingLocalTaskAction;
  expiresAt: number;
  expectedAssignee?: string | null;
};

const PENDING_LOCAL_ACTION_TTL_MS = 15000;
const DEBUG_TASK_STORE =
  import.meta.env.DEV || import.meta.env.VITE_DEBUG_TASK_STORE === 'true';
const LOAD_TASKS_PERFORMANCE_PREFIX = 'task-list.loadTasks';
const FIRST_TASKS_READY_MARK = 'task-list.first-tasks-ready';
const loadTasksInputSchema = z.strictObject({
  silent: z.boolean().optional(),
  reason: z.string().optional(),
});
const taskStoreStringSchema = z.string();
const pendingLocalTaskActions = new Map<string, PendingLocalTaskActionEntry>();
const taskSyncTokens = new Map<string, symbol>();
let loadTasksRequestSequence = 0;
let activeLoadTasksPromise: Promise<void> | null = null;
let activeLoadTasksContext: {
  requestId: number;
  silent: boolean;
  reasons: Set<string>;
  dirty: boolean;
  dirtyReasons: Set<string>;
  taskMutation: boolean;
  startedAt: number;
} | null = null;
let hasMarkedFirstTasksReady = false;

const fallbackInactiveTaskNotice: TaskInactiveNotice = {
  reason: 'not_found',
  title: 'Esta tarea ya no esta disponible',
  description: 'Camunda no la encontro como tarea activa ni devolvio un historico claro para este enlace.',
};

const historyDateFormatter = new Intl.DateTimeFormat('es-ES', {
  dateStyle: 'short',
  timeStyle: 'short',
});

function normalizeLoadTasksOptions(input: LoadTasksInput = {}): LoadTasksOptions {
  const parsed = loadTasksInputSchema.safeParse(input);
  return parsed.success ? parsed.data : {};
}

function shouldQueueFollowUpLoadTasks(reason: string): boolean {
  return reason !== 'hook-initial';
}

function markTaskPerformance(name: string): void {
  const performanceRef = globalThis.performance;
  if (!performanceRef) {
    return;
  }

  try {
    performanceRef.mark(name);
  } catch {
  }
}

function measureTaskPerformance(name: string, startMark: string, endMark: string): void {
  const performanceRef = globalThis.performance;
  if (!performanceRef) {
    return;
  }

  try {
    performanceRef.measure(name, startMark, endMark);
  } catch {
  }
}

function getSafeEndpointPath(endpoint: string): string {
  const withoutQuery = endpoint.split('?')[0] || endpoint || '/';
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}

interface TaskStoreErrorSummary {
  name: string;
  status?: number;
  statusText?: string;
  endpoint?: string;
  message?: string;
}

interface TaskStoreDebugMeta {
  requestId?: number;
  reason?: string;
  dirty?: boolean;
  silent?: boolean;
  durationMs?: number;
  reasons?: string[];
  dirtyReasons?: string[];
}

function summarizeTaskStoreError(error: Error): TaskStoreErrorSummary {
  if (isCamundaRequestError(error)) {
    return {
      name: error.name,
      status: error.status,
      statusText: error.statusText,
      endpoint: getSafeEndpointPath(error.endpoint),
    };
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
    };
  }

  return { name: 'Error', message: 'Error desconocido' };
}

function taskStoreDebug(message: string, meta?: TaskStoreDebugMeta): void {
  if (!DEBUG_TASK_STORE) {
    return;
  }

  if (meta) {
    console.debug(message, meta);
    return;
  }

  console.debug(message);
}

function formatHistoryDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return historyDateFormatter.format(date);
}

function buildHistoricTaskNotice(
  task: Awaited<ReturnType<typeof CamundaHistoryService.getHistoricTaskInstances>>[number] | undefined,
  assigneeLabel?: string | null,
): TaskInactiveNotice {
  if (!task) {
    return fallbackInactiveTaskNotice;
  }

  const assigneeText = assigneeLabel ? ` Estaba asignada a ${assigneeLabel}.` : '';
  const finishedAt = formatHistoryDate(task.endTime);
  const deleteReason = task.deleteReason?.trim() || null;
  const normalizedDeleteReason = deleteReason?.toLowerCase() ?? '';
  const timestamp = finishedAt ? ` el ${finishedAt}` : '';

  if (task.endTime && normalizedDeleteReason.includes('completed')) {
    return {
      reason: 'completed',
      title: 'Esta tarea ya fue enviada',
      description: `Quedo completada${timestamp}.${assigneeText} Ya no se puede editar.`,
    };
  }

  if (
    task.endTime &&
    (
      normalizedDeleteReason.includes('delete') ||
      normalizedDeleteReason.includes('cancel') ||
      normalizedDeleteReason.includes('terminate')
    )
  ) {
    return {
      reason: 'cancelled',
      title: 'Esta tarea fue cancelada',
      description: `Camunda la cerro${timestamp}.${assigneeText} Motivo: ${deleteReason}.`,
    };
  }

  if (task.endTime) {
    return {
      reason: 'ended',
      title: 'Esta tarea ya termino en Camunda',
      description: `Camunda la cerro${timestamp}.${assigneeText}${deleteReason ? ` Motivo: ${deleteReason}.` : ''}`,
    };
  }

  return {
    reason: 'not_visible',
    title: 'Esta tarea ya no esta disponible para tu usuario',
    description: `Camunda la conserva abierta, pero no aparece disponible para tu sesion actual.${assigneeText}`,
  };
}

async function resolveInactiveTaskNotice(taskId: string): Promise<TaskInactiveNotice> {
  const historicTasks = await CamundaHistoryService.getHistoricTaskInstances({
    taskId,
    maxResults: 1,
  });
  const historicTask = historicTasks[0];
  let assigneeLabel = historicTask?.assignee ?? null;

  if (historicTask?.assignee) {
    try {
      const user = await CamundaUserService.getUserById(historicTask.assignee);
      if (user) {
        const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
        assigneeLabel = fullName || user.id;
      }
    } catch {
      assigneeLabel = historicTask.assignee;
    }
  }

  return buildHistoricTaskNotice(historicTask, assigneeLabel);
}

export function markPendingLocalTaskAction(
  taskId: string,
  action: PendingLocalTaskAction,
  expectedAssignee?: string | null,
) {
  pendingLocalTaskActions.set(taskId, {
    action,
    expectedAssignee,
    expiresAt: Date.now() + PENDING_LOCAL_ACTION_TTL_MS,
  });
}

export function consumePendingLocalTaskAction(
  taskId: string,
  actions?: PendingLocalTaskAction[],
  expectedAssignee?: string | null,
): PendingLocalTaskAction | null {
  const entry = pendingLocalTaskActions.get(taskId);
  if (!entry) return null;
  if (entry.expiresAt < Date.now()) {
    pendingLocalTaskActions.delete(taskId);
    return null;
  }
  if (actions && !actions.includes(entry.action)) {
    return null;
  }
  if (expectedAssignee !== undefined && entry.expectedAssignee !== expectedAssignee) {
    return null;
  }
  pendingLocalTaskActions.delete(taskId);
  return entry.action;
}

export function hasPendingLocalTaskAction(
  taskId: string,
  actions?: PendingLocalTaskAction[],
  expectedAssignee?: string | null,
): boolean {
  const entry = pendingLocalTaskActions.get(taskId);
  if (!entry) return false;
  if (entry.expiresAt < Date.now()) {
    pendingLocalTaskActions.delete(taskId);
    return false;
  }
  if (actions && !actions.includes(entry.action)) {
    return false;
  }
  if (expectedAssignee !== undefined && entry.expectedAssignee !== expectedAssignee) {
    return false;
  }
  return true;
}

function taskActionResultFromError(error: Error, fallbackMessage: string, eventId?: string): TaskActionResult {
  if (isTaskNotActiveError(error)) {
    return {
      ok: false,
      reason: 'task_not_active',
      message: 'Esta tarea ya no esta activa. Puede que ya se haya enviado, reasignado o reemplazado.',
      eventId,
    };
  }
  if (isAuthError(error)) {
    return {
      ok: false,
      reason: 'auth_error',
      message: 'Tu sesion no esta autorizada o ha caducado. Vuelve a iniciar sesion.',
      eventId,
    };
  }
  if (isConcurrentTaskUpdateError(error)) {
    return {
      ok: false,
      reason: 'conflict',
      message: 'La tarea cambio en Camunda mientras intentabas actualizarla. Hemos refrescado el estado.',
      eventId,
    };
  }
  return { ok: false, reason: 'error', message: fallbackMessage, eventId };
}

function buildTaskTelemetryContext(taskId: string, task?: CamundaTask | null, assignee?: string | null) {
  return {
    taskId,
    processInstanceId: task?.processInstanceId,
    processDefinitionId: task?.processDefinitionId,
    processKey: task?.processDefinitionKey,
    taskDefinitionKey: task?.taskDefinitionKey,
    formKey: task?.formKey ?? undefined,
    assignee: assignee === undefined ? task?.assignee ?? undefined : assignee ?? undefined,
  };
}

function getTelemetryNow(): number {
  return globalThis.performance?.now?.() ?? Date.now();
}

function getAssignmentActionDurationMs(startedAt: number): number {
  return Math.max(0, Math.round(getTelemetryNow() - startedAt));
}

function getCamundaHttpContext(error: Error) {
  if (!isCamundaRequestError(error)) {
    return undefined;
  }

  return {
    endpoint: getSafeEndpointPath(error.endpoint),
    status: error.status,
    statusText: error.statusText,
  };
}

function startAssignmentTelemetry(action: AssignmentTelemetryAction, eventId: string, taskId: string, task: CamundaTask | null, assignee?: string | null): number {
  const startedAt = getTelemetryNow();
  trackActionStarted({
    eventId,
    action,
    bpm: buildTaskTelemetryContext(taskId, task, assignee),
  });
  return startedAt;
}

function taskSelectionErrorResult(error: Error): TaskSelectionResult {
  if (isTaskNotActiveError(error)) {
    return { status: 'not_active', notice: fallbackInactiveTaskNotice };
  }
  if (isAuthError(error)) {
    return {
      status: 'error',
      reason: 'auth_error',
      message: 'Tu sesion no esta autorizada o ha caducado. Vuelve a iniciar sesion.',
    };
  }
  return {
    status: 'error',
    reason: 'error',
    message: error instanceof Error ? error.message : 'Error loading task',
  };
}

function areStringListsEqual(left?: string[], right?: string[]): boolean {
  if (left === right) return true;
  if (!left || !right) return !left && !right;
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function areTaskListItemsEqual(left: CamundaTask, right: CamundaTask): boolean {
  return left.id === right.id
    && left.name === right.name
    && left.processDefinitionId === right.processDefinitionId
    && left.processDefinitionKey === right.processDefinitionKey
    && left.processDefinitionName === right.processDefinitionName
    && left.processInstanceId === right.processInstanceId
    && left.assignee === right.assignee
    && left.assignee_fullname === right.assignee_fullname
    && left.priority === right.priority
    && left.created === right.created
    && left.formKey === right.formKey
    && left.taskDefinitionKey === right.taskDefinitionKey
    && areStringListsEqual(left.candidateGroups, right.candidateGroups)
    && areStringListsEqual(left.candidateUsers, right.candidateUsers);
}

function mergeTaskListsPreservingIdentity(
  currentTasks: CamundaTask[],
  nextTasks: CamundaTask[],
): CamundaTask[] {
  const currentById = new Map(currentTasks.map((task) => [task.id, task]));
  let changed = currentTasks.length !== nextTasks.length;

  const mergedTasks = nextTasks.map((nextTask, index) => {
    const currentTask = currentById.get(nextTask.id);
    if (currentTask && areTaskListItemsEqual(currentTask, nextTask)) {
      if (currentTasks[index] !== currentTask) {
        changed = true;
      }
      return currentTask;
    }
    changed = true;
    return nextTask;
  });

  return changed ? mergedTasks : currentTasks;
}

function markActiveLoadTasksDirtyFromTaskEvent(): void {
  if (!activeLoadTasksContext) {
    return;
  }

  activeLoadTasksContext.dirty = true;
  activeLoadTasksContext.taskMutation = true;
  activeLoadTasksContext.dirtyReasons.add('realtime:task-event');
}

function beginTaskSync(taskId: string): symbol {
  const token = Symbol(taskId);
  taskSyncTokens.set(taskId, token);
  return token;
}

function isCurrentTaskSync(taskId: string, token: symbol): boolean {
  return taskSyncTokens.get(taskId) === token;
}

function invalidateTaskSync(taskId: string): void {
  taskSyncTokens.delete(taskId);
}

function upsertTaskListItem(currentTasks: CamundaTask[], nextTask: CamundaTask): CamundaTask[] {
  const taskIndex = currentTasks.findIndex((task) => task.id === nextTask.id);
  if (taskIndex >= 0) {
    const existingTask = currentTasks[taskIndex];
    if (areTaskListItemsEqual(existingTask, nextTask)) {
      return currentTasks;
    }

    const tasks = [...currentTasks];
    tasks[taskIndex] = nextTask;
    return tasks.sort((left, right) => new Date(right.created).getTime() - new Date(left.created).getTime());
  }

  return [...currentTasks, nextTask]
    .sort((left, right) => new Date(right.created).getTime() - new Date(left.created).getTime());
}

function removeTaskListItem(currentTasks: CamundaTask[], taskId: string): CamundaTask[] {
  const taskIndex = currentTasks.findIndex((task) => task.id === taskId);
  if (taskIndex < 0) {
    return currentTasks;
  }

  return currentTasks.filter((task) => task.id !== taskId);
}

export const useTaskStore = create<TaskStore>()(devtools(
  (set, get) => ({
    tasks: [],
    currentTask: null,
    isLoading: false,
    isInitialLoad: true,
    isTaskListRefreshing: false,
    error: null,
    realtimeConnected: false,
    realtimeError: null,
    
    loadTasks: async (input: LoadTasksInput = {}) => {
      const options = normalizeLoadTasksOptions(input);
      const reason = options.reason ?? 'direct';
      const showLoading = !options.silent;

      if (activeLoadTasksPromise && activeLoadTasksContext) {
        activeLoadTasksContext.reasons.add(reason);
        if (shouldQueueFollowUpLoadTasks(reason)) {
          activeLoadTasksContext.dirty = true;
          activeLoadTasksContext.dirtyReasons.add(reason);
        }
        if (showLoading && activeLoadTasksContext.silent) {
          activeLoadTasksContext.silent = false;
          set({
            isLoading: true,
            isTaskListRefreshing: true,
            error: null,
          });
        }
        taskStoreDebug('[task-store] loadTasks dedupe', {
          requestId: activeLoadTasksContext.requestId,
          reason,
          dirty: activeLoadTasksContext.dirty,
          silent: !showLoading,
        });
        markTaskPerformance('task-store.loadTasks.dedupe');
        return activeLoadTasksPromise;
      }

      const requestId = ++loadTasksRequestSequence;
      const loadStartMark = `${LOAD_TASKS_PERFORMANCE_PREFIX}.${requestId}.start`;
      const loadEndMark = `${LOAD_TASKS_PERFORMANCE_PREFIX}.${requestId}.end`;
      const context = {
        requestId,
        silent: options.silent === true,
        reasons: new Set([reason]),
        dirty: false,
        dirtyReasons: new Set<string>(),
        taskMutation: false,
        startedAt: Date.now(),
      };

      activeLoadTasksContext = context;
      markTaskPerformance(loadStartMark);
      set(state => ({
        isTaskListRefreshing: true,
        error: context.silent ? state.error : null,
      }));

      const loadPromise = Promise.resolve().then(async () => {
        taskStoreDebug('[task-store] loadTasks start', {
          requestId,
          reason,
          silent: context.silent,
        });
        markTaskPerformance('task-store.loadTasks.start');

        if (!context.silent) {
          set({ isLoading: true, error: null });
        }

        try {
          const currentUserId = CamundaUserService.getCurrentUserId();
          if (!currentUserId) {
            throw new Error('No hay usuario autenticado');
          }
          const rawTasks = await CamundaTaskService.getAvailableTasksForUser(currentUserId);
          const tasks = (Array.isArray(rawTasks) ? rawTasks : []).filter(
            (task): task is CamundaTask => {
              if (task == null) {
                return false;
              }
              const parsedTaskId = taskStoreStringSchema.safeParse(task.id);
              return parsedTaskId.success && parsedTaskId.data.trim().length > 0;
            },
          );
          if (requestId !== loadTasksRequestSequence) {
            markTaskPerformance(loadEndMark);
            measureTaskPerformance(`${LOAD_TASKS_PERFORMANCE_PREFIX}.duration`, loadStartMark, loadEndMark);
            return;
          }
          if (context.taskMutation) {
            set({
              isLoading: false,
              isInitialLoad: false,
              isTaskListRefreshing: false,
            });
            markTaskPerformance(loadEndMark);
            measureTaskPerformance(`${LOAD_TASKS_PERFORMANCE_PREFIX}.duration`, loadStartMark, loadEndMark);
            return;
          }
          const shouldMarkFirstTasksReady = !hasMarkedFirstTasksReady && tasks.length > 0;
          set(state => ({
            tasks: mergeTaskListsPreservingIdentity(state.tasks, tasks),
            isLoading: false,
            isInitialLoad: false,
            isTaskListRefreshing: false,
            error: null
          }));
          markTaskPerformance(loadEndMark);
          measureTaskPerformance(`${LOAD_TASKS_PERFORMANCE_PREFIX}.duration`, loadStartMark, loadEndMark);
          if (shouldMarkFirstTasksReady) {
            hasMarkedFirstTasksReady = true;
            markTaskPerformance(FIRST_TASKS_READY_MARK);
            measureTaskPerformance('task-list.loadTasks.to-first-tasks-ready', loadStartMark, FIRST_TASKS_READY_MARK);
          }
        } catch (error) {
          const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
          if (requestId !== loadTasksRequestSequence) {
            markTaskPerformance(loadEndMark);
            measureTaskPerformance(`${LOAD_TASKS_PERFORMANCE_PREFIX}.duration`, loadStartMark, loadEndMark);
            return;
          }
          markTaskPerformance(loadEndMark);
          measureTaskPerformance(`${LOAD_TASKS_PERFORMANCE_PREFIX}.duration`, loadStartMark, loadEndMark);
          console.error('Error loading tasks:', summarizeTaskStoreError(normalizedError));
          if (context.silent) {
            set({
              isLoading: false,
              isInitialLoad: false,
              isTaskListRefreshing: false,
            });
            return;
          }
          set(state => ({
            tasks: state.tasks.length > 0 ? state.tasks : [],
            error: normalizedError.message || 'Error loading tasks',
            isLoading: false,
            isTaskListRefreshing: false,
            isInitialLoad: false
          }));
        } finally {
          const durationMs = Date.now() - context.startedAt;
          taskStoreDebug('[task-store] loadTasks end', {
            requestId,
            durationMs,
            reasons: Array.from(context.reasons),
            dirtyReasons: Array.from(context.dirtyReasons),
            silent: context.silent,
          });
          markTaskPerformance('task-store.loadTasks.end');
        }
      });

      activeLoadTasksPromise = loadPromise;

      try {
        await loadPromise;
      } finally {
        let followUpReason: string | null = null;
        if (activeLoadTasksContext === context) {
          followUpReason = context.dirty
            ? `dirty:${Array.from(context.dirtyReasons).join(',') || 'refresh'}`
            : null;
          activeLoadTasksPromise = null;
          activeLoadTasksContext = null;
        }

        if (followUpReason) {
          taskStoreDebug('[task-store] loadTasks dirty follow-up', {
            requestId,
            reason: followUpReason,
          });
          markTaskPerformance('task-store.loadTasks.dirty-follow-up');
          await get().loadTasks({
            silent: true,
            reason: followUpReason,
          });
        }
      }
    },
    
    selectTask: async (taskId: string) => {
      const { tasks } = get();
      const existingTask = tasks.find(t => t.id === taskId);
      
      if (existingTask) {
        set({
          currentTask: existingTask,
          error: null,
        });
      }
      
      set({ isLoading: !existingTask, error: null });
      try {
        const currentUserId = CamundaUserService.getCurrentUserId();
        if (!currentUserId) {
          throw new Error('No hay usuario autenticado');
        }
        const task = await CamundaTaskService.getAvailableTaskForUserById(taskId, currentUserId);
        if (!task) {
          const notice = await resolveInactiveTaskNotice(taskId);
          set(state => ({
            tasks: state.tasks.filter(t => t.id !== taskId),
            currentTask: state.currentTask?.id === taskId ? null : state.currentTask,
            isLoading: false,
            error: null,
          }));
          return { status: 'not_active', notice };
        }

        set(state => {
          const taskIndex = state.tasks.findIndex(t => t.id === taskId);
          const nextTasks = [...state.tasks];
          if (taskIndex >= 0) {
            nextTasks[taskIndex] = task;
          } else {
            nextTasks.unshift(task);
          }

          return {
            tasks: nextTasks,
            currentTask: task,
            isLoading: false,
            error: null,
          };
        });
        return { status: 'ready', task };
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        console.error('Error selecting task:', summarizeTaskStoreError(normalizedError));
        const result = isTaskNotActiveError(normalizedError)
          ? { status: 'not_active' as const, notice: await resolveInactiveTaskNotice(taskId) }
          : taskSelectionErrorResult(normalizedError);
        set({ 
          currentTask: null,
          error: result.status === 'error' ? result.message : null,
          isLoading: false 
        });
        return result;
      }
    },
    
    clearCurrentTask: () => {
      set({ currentTask: null, error: null });
    },
    claimTask: async (taskId: string) => {
      const eventId = createBpmEventId();
      const currentUserId = CamundaUserService.getCurrentUserId();
      const { tasks, currentTask } = get();
      const taskToUpdate = tasks.find(t => t.id === taskId) || (currentTask?.id === taskId ? currentTask : null);
      const startedAt = startAssignmentTelemetry('claim', eventId, taskId, taskToUpdate, currentUserId);
      if (!currentUserId) {
        trackActionFailed({
          eventId,
          action: 'claim',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate),
          durationMs: getAssignmentActionDurationMs(startedAt),
          error: 'No hay usuario autenticado',
        });
        return {
          ok: false,
          reason: 'auth_error',
          message: 'No hay usuario autenticado',
          eventId,
        };
      }
      
      try {
        markPendingLocalTaskAction(taskId, 'claim', currentUserId);
        const success = await CamundaTaskService.claimTask(taskId, currentUserId);
        if (success) {
          await get().forceSyncTaskState(taskId);
          trackActionSucceeded({
            eventId,
            action: 'claim',
            bpm: buildTaskTelemetryContext(taskId, taskToUpdate, currentUserId),
            durationMs: getAssignmentActionDurationMs(startedAt),
          });
          return { ok: true };
        }
        consumePendingLocalTaskAction(taskId, ['claim']);
        trackActionFailed({
          eventId,
          action: 'claim',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate, currentUserId),
          durationMs: getAssignmentActionDurationMs(startedAt),
          error: 'CamundaTaskService.claimTask returned false',
        });
        return { ok: false, reason: 'error', message: 'No se pudo reclamar la tarea.', eventId };
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        consumePendingLocalTaskAction(taskId, ['claim']);
        console.error('Error claiming task:', summarizeTaskStoreError(normalizedError));
        if (isConcurrentTaskUpdateError(normalizedError)) {
          await get().forceSyncTaskState(taskId);
        }
        trackActionFailed({
          eventId,
          action: 'claim',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate, currentUserId),
          durationMs: getAssignmentActionDurationMs(startedAt),
          http: getCamundaHttpContext(normalizedError),
          error: normalizedError.message,
        });
        return taskActionResultFromError(normalizedError, 'No se pudo reclamar la tarea.', eventId);
      }
    },

    forceSyncTaskState: async (taskId: string) => {
      const currentUserId = CamundaUserService.getCurrentUserId();
      if (!currentUserId) {
        return false;
      }
      const syncToken = beginTaskSync(taskId);

      try {
        const taskDetails = await CamundaTaskService.getAvailableTaskForUserById(taskId, currentUserId);
        if (!isCurrentTaskSync(taskId, syncToken)) {
          return false;
        }
        if (taskDetails) {
          set(state => {
            const existingTask = state.tasks.find((task) => task.id === taskId);
            const nextTask = existingTask && areTaskListItemsEqual(existingTask, taskDetails)
              ? existingTask
              : taskDetails;

            return {
              tasks: upsertTaskListItem(state.tasks, taskDetails),
              currentTask: state.currentTask?.id === taskId ? nextTask : state.currentTask,
              error: null,
            };
          });
          return true;
        }

        set(state => ({
          tasks: removeTaskListItem(state.tasks, taskId),
          currentTask: state.currentTask?.id === taskId ? null : state.currentTask,
          error: null,
        }));
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        if (isCurrentTaskSync(taskId, syncToken)) {
          console.error('Error syncing task state:', summarizeTaskStoreError(normalizedError));
        }
      } finally {
        if (isCurrentTaskSync(taskId, syncToken)) {
          taskSyncTokens.delete(taskId);
        }
      }
      return false;
    },

    handleRealtimeTaskEvent: async (event: TaskRealtimeTaskEvent) => {
      markActiveLoadTasksDirtyFromTaskEvent();
      if (event.type === 'TASK_REMOVE') {
        invalidateTaskSync(event.taskId);
        if (hasPendingLocalTaskAction(event.taskId, ['complete'])) {
          return;
        }
        set(state => ({
          tasks: removeTaskListItem(state.tasks, event.taskId),
          currentTask: state.currentTask?.id === event.taskId ? null : state.currentTask,
        }));
        return;
      }

      await get().forceSyncTaskState(event.taskId);
    },

    unclaimTask: async (taskId: string) => {
      const eventId = createBpmEventId();
      const currentUserId = CamundaUserService.getCurrentUserId();
      const { tasks, currentTask } = get();
      const taskToUpdate = tasks.find(t => t.id === taskId) || (currentTask?.id === taskId ? currentTask : null);
      const startedAt = startAssignmentTelemetry('unclaim', eventId, taskId, taskToUpdate, null);
      if (!currentUserId) {
        trackActionFailed({
          eventId,
          action: 'unclaim',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate),
          durationMs: getAssignmentActionDurationMs(startedAt),
          error: 'No hay usuario autenticado',
        });
        return {
          ok: false,
          reason: 'auth_error',
          message: 'No hay usuario autenticado',
          eventId,
        };
      }
      
      try {
        markPendingLocalTaskAction(taskId, 'unclaim', null);
        const success = await CamundaTaskService.unclaimTask(taskId);
        if (success) {
          await get().forceSyncTaskState(taskId);
          trackActionSucceeded({
            eventId,
            action: 'unclaim',
            bpm: buildTaskTelemetryContext(taskId, taskToUpdate, null),
            durationMs: getAssignmentActionDurationMs(startedAt),
          });
          return { ok: true };
        }
        consumePendingLocalTaskAction(taskId, ['unclaim']);
        trackActionFailed({
          eventId,
          action: 'unclaim',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate, null),
          durationMs: getAssignmentActionDurationMs(startedAt),
          error: 'CamundaTaskService.unclaimTask returned false',
        });
        return { ok: false, reason: 'error', message: 'No se pudo liberar la tarea.', eventId };
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        consumePendingLocalTaskAction(taskId, ['unclaim']);
        console.error('Error unclaiming task:', summarizeTaskStoreError(normalizedError));
        if (isConcurrentTaskUpdateError(normalizedError)) {
          await get().forceSyncTaskState(taskId);
        }
        trackActionFailed({
          eventId,
          action: 'unclaim',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate, null),
          durationMs: getAssignmentActionDurationMs(startedAt),
          http: getCamundaHttpContext(normalizedError),
          error: normalizedError.message,
        });
        return taskActionResultFromError(normalizedError, 'No se pudo liberar la tarea.', eventId);
      }
    },
    
    reassignTask: async (taskId: string, newAssigneeId: string) => {
      const eventId = createBpmEventId();
      const currentUserId = CamundaUserService.getCurrentUserId();
      const { tasks, currentTask } = get();
      const taskToUpdate = tasks.find(t => t.id === taskId) || (currentTask?.id === taskId ? currentTask : null);
      const startedAt = startAssignmentTelemetry('reassign', eventId, taskId, taskToUpdate, newAssigneeId);
      if (!currentUserId) {
        trackActionFailed({
          eventId,
          action: 'reassign',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate, newAssigneeId),
          durationMs: getAssignmentActionDurationMs(startedAt),
          error: 'No hay usuario autenticado',
        });
        return {
          ok: false,
          reason: 'auth_error',
          message: 'No hay usuario autenticado',
          eventId,
        };
      }
      
      if (!taskToUpdate) {
        console.error(`Task ${taskId} not found in local state`);
        trackActionFailed({
          eventId,
          action: 'reassign',
          bpm: buildTaskTelemetryContext(taskId, null, newAssigneeId),
          durationMs: getAssignmentActionDurationMs(startedAt),
          error: 'Task not found in local state',
        });
        return {
          ok: false,
          reason: 'task_not_active',
          message: 'Esta tarea ya no esta activa. Puede que ya se haya enviado, reasignado o reemplazado.',
          eventId,
        };
      }
      
      try {
        markPendingLocalTaskAction(taskId, 'reassign', newAssigneeId);
        const success = await CamundaTaskService.reassignTask(taskId, newAssigneeId);
        
        if (success) {
          await get().forceSyncTaskState(taskId);
          trackActionSucceeded({
            eventId,
            action: 'reassign',
            bpm: buildTaskTelemetryContext(taskId, taskToUpdate, newAssigneeId),
            durationMs: getAssignmentActionDurationMs(startedAt),
          });
          return { ok: true };
        }
        
        consumePendingLocalTaskAction(taskId, ['reassign']);
        trackActionFailed({
          eventId,
          action: 'reassign',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate, newAssigneeId),
          durationMs: getAssignmentActionDurationMs(startedAt),
          error: 'CamundaTaskService.reassignTask returned false',
        });
        return { ok: false, reason: 'error', message: 'No se pudo reasignar la tarea.', eventId };
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        consumePendingLocalTaskAction(taskId, ['reassign']);
        console.error('Error reassigning task:', summarizeTaskStoreError(normalizedError));
        if (isConcurrentTaskUpdateError(normalizedError)) {
          await get().forceSyncTaskState(taskId);
        }
        trackActionFailed({
          eventId,
          action: 'reassign',
          bpm: buildTaskTelemetryContext(taskId, taskToUpdate, newAssigneeId),
          durationMs: getAssignmentActionDurationMs(startedAt),
          http: getCamundaHttpContext(normalizedError),
          error: normalizedError.message,
        });
        return taskActionResultFromError(normalizedError, 'No se pudo reasignar la tarea.', eventId);
      }
    },
    setRealtimeStatus: (status) => {
      set({
        realtimeConnected: status.connected,
        realtimeError: status.error,
      });
    },

    canInteractWithTask: (taskId: string) => {
      const { tasks, currentTask } = get();
      const currentUserId = CamundaUserService.getCurrentUserId();
      const task = tasks.find(t => t.id === taskId) || (currentTask?.id === taskId ? currentTask : null);
      return task?.assignee === currentUserId;
    },
    
    isTaskAssignedToCurrentUser: (taskId: string) => {
      const { tasks, currentTask } = get();
      const currentUserId = CamundaUserService.getCurrentUserId();
      const task = tasks.find(t => t.id === taskId) || (currentTask?.id === taskId ? currentTask : null);
      return task?.assignee === currentUserId;
    },
    
    getTaskById: (taskId: string) => {
      const { tasks, currentTask } = get();
      return tasks.find(t => t.id === taskId) || (currentTask?.id === taskId ? currentTask : null);
    },
    
  }),
  { name: 'task-store' }
));

export const useTaskList = () => useTaskStore(state => state.tasks);
export const useTaskInitialLoad = () => useTaskStore(state => state.isInitialLoad);
export const useTaskListRefreshing = () => useTaskStore(state => state.isTaskListRefreshing);
export const useTaskError = () => useTaskStore(state => state.error);


export const useTaskCountForCurrentUser = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const [taskCount, setTaskCount] = React.useState<number>(0);
  const [isLoading, setIsLoading] = React.useState(true);
  
  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;

    const fetchTaskCount = async () => {
      try {
        setIsLoading(true);
        const userId = CamundaUserService.getCurrentUserId();
        if (userId) {
          const count = await CamundaTaskService.getTaskCountForUser(userId);
          if (!cancelled) {
            setTaskCount(count);
          }
        }
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error('Error desconocido');
        console.error('Error fetching task count:', summarizeTaskStoreError(normalizedError));
        if (!cancelled) {
          setTaskCount(0);
        }
      }
      if (!cancelled) {
        setIsLoading(false);
      }
    };
    
    void fetchTaskCount();

    return () => {
      cancelled = true;
    };
  }, [enabled]);
  
  return { taskCount, isLoading: enabled ? isLoading : false };
};



export const useLoadTasks = () => useTaskStore(state => state.loadTasks);
export const useSelectTask = () => useTaskStore(state => state.selectTask);
export const useClearCurrentTask = () => useTaskStore(state => state.clearCurrentTask);

export const useClaimTask = () => useTaskStore(state => state.claimTask);
export const useUnclaimTask = () => useTaskStore(state => state.unclaimTask);
export const useReassignTask = () => useTaskStore(state => state.reassignTask);

export const useRealtimeConnected = () => useTaskStore(state => state.realtimeConnected);
export const useRealtimeError = () => useTaskStore(state => state.realtimeError);

export const useTaskStoreOptimized = () => {
  const currentTask = useTaskStore(state => state.currentTask);
  const isTaskLoading = useTaskStore(state => state.isLoading);
  const taskError = useTaskStore(state => state.error);

  return {
    currentTask,
    isTaskLoading,
    taskError,
  };
};



export type { TaskStore } from './task-store.types';
